import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { DollarSign, TrendingUp } from "lucide-react";
import { apiGet } from "@/lib/api";
const axisTick = { fill: "#64748b", fontSize: 10, fontFamily: "JetBrains Mono" };
const chartMargin = { top: 10, right: 10, left: 0, bottom: 0 };
const tooltipStyle = { background: "rgba(5,9,20,0.95)", border: "1px solid rgba(0,240,255,0.3)", borderRadius: 6, fontFamily: "JetBrains Mono", fontSize: 11 };
const legendStyle = { fontFamily: "Chivo Mono", fontSize: 10, textTransform: "uppercase", letterSpacing: 1 };
const rise = { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 } };

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function RevenuePage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = () => apiGet("/revenue?days=14").then(setData).catch(() => {});
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);

  if (!data) {
    return (
      <div className="h-full flex items-center justify-center font-mono text-slate-500 tracking-widest">
        LOADING REVENUE…
      </div>
    );
  }

  if (data.status !== "connected") return <div className="h-full overflow-y-auto p-5 lg:p-6" data-testid="revenue-not-connected">
    <div className="flex items-center gap-3 mb-6"><DollarSign className="w-6 h-6 text-jarvis-cyan" /><h1 className="font-mono text-xl uppercase tracking-[0.25em] text-jarvis-cyan">Revenue</h1></div>
    <section className="jv-panel p-6 space-y-3"><h2 className="text-base text-slate-200">Not connected</h2><p className="text-sm text-slate-400">No verified revenue source is connected. Generated earnings and trends have been removed from this view.</p><p className="text-xs text-slate-500">Existing stored records are preserved. No financial amounts are inferred from them.</p></section>
  </div>;

  const businesses = (data.businesses || []).map((business, index) => ({ ...business,
    color: ["#18c9ff", "#8daeff", "#ffd34e", "#7694a7"][index % 4] }));
  const dates = businesses[0]?.history.map((h) => h.date) || [];
  const chartData = dates.map((d, idx) => {
    const row = { date: d.slice(5) };
    businesses.forEach((b) => {
      row[b.name] = b.history[idx]?.amount || 0;
    });
    return row;
  });

  return (
    <div className="h-full overflow-y-auto p-5 lg:p-6">
      <div className="flex items-center gap-3 mb-6">
        <DollarSign className="w-6 h-6 text-jarvis-green" />
        <div>
          <h1 className="font-mono text-xl lg:text-2xl uppercase tracking-[0.25em] font-bold text-jarvis-green drop-shadow-[0_0_12px_rgba(24,201,255,0.25)]">
            Revenue
          </h1>
          <p className="font-mono text-[11px] text-slate-500 tracking-widest">Daily earnings across every business</p>
        </div>
      </div>

      {/* Top summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 mb-5">
        <motion.div {...rise} className="jv-panel p-4 xl:col-span-1" data-testid="revenue-today-total">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1 flex items-center gap-1.5">
            <TrendingUp className="w-3.5 h-3.5 text-jarvis-green" /> Today Total
          </div>
          <div className="font-mono text-3xl font-bold text-jarvis-green">{money(data.today_total)}</div>
          <div className="font-mono text-[10px] text-slate-500 mt-1">{money(data.period_total)} over {data.days}d</div>
        </motion.div>

        {businesses.map((b) => (
          <motion.div
            key={b.name}
            {...rise}
            className="jv-panel p-4"
            data-testid={`revenue-card-${b.name}`}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="font-mono text-[10px] uppercase tracking-[0.15em]" style={{ color: b.color }}>{b.name}</span>
              <span className="w-2 h-2 rounded-full jv-pulse" style={{ backgroundColor: b.color, color: b.color }} />
            </div>
            <div className="font-mono text-2xl font-bold text-slate-100">{money(b.today)}</div>
            <div className="h-10 mt-1 -mx-1">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={b.history.map((h) => ({ v: h.amount }))}>
                  <defs>
                    <linearGradient id={`sp-${b.name}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={b.color} stopOpacity={0.6} />
                      <stop offset="100%" stopColor={b.color} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area type="monotone" dataKey="v" stroke={b.color} strokeWidth={1.5} fill={`url(#sp-${b.name})`} isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Combined chart */}
      <div className="jv-panel p-5" data-testid="revenue-chart">
        <h3 className="font-mono text-sm uppercase tracking-[0.2em] text-jarvis-cyanBright mb-4">14-Day Revenue Trend</h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={chartMargin}>
              <defs>
                {businesses.map((b) => (
                  <linearGradient key={b.name} id={`grad-${b.name}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={b.color} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={b.color} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" tick={axisTick} />
              <YAxis tick={axisTick} tickFormatter={(v) => `$${v / 1000}k`} />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(v) => money(v)}
              />
              <Legend wrapperStyle={legendStyle} />
              {businesses.map((b) => (
                <Area
                  key={b.name}
                  type="monotone"
                  dataKey={b.name}
                  stroke={b.color}
                  strokeWidth={2}
                  fill={`url(#grad-${b.name})`}
                  isAnimationActive={false}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
